import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { apiFetch } from '../constants';

const DEFAULT_CENTER = [47.5162, 14.5501];
const DEFAULT_ZOOM = 7;

const LEGEND = [
  { color: '#a78bfa', label: 'Power Flow done' },
  { color: '#60a5fa', label: 'MV Grid done' },
  { color: '#4ade80', label: 'LV Grid done' },
  { color: '#94a3b8', label: 'Graph only' },
];

function stepColor(props) {
  if (props?.has_pf) return '#a78bfa';
  if (props?.has_mv) return '#60a5fa';
  if (props?.has_lv) return '#4ade80';
  return '#94a3b8';
}

// Split "KG_Gemeinde_Bundesland" into three parts
function nameParts(name) {
  const p = (name || '').split('_');
  return {
    kg:         p[0] || name || '',
    gemeinde:   p[1] || '',
    bundesland: p.slice(2).join('_') || '',
  };
}

function AreaName({ name, style = {} }) {
  const { kg, gemeinde, bundesland } = nameParts(name);
  return (
    <div style={style}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', lineHeight: 1.3 }}>{kg}</div>
      {gemeinde   && <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.3 }}>{gemeinde}</div>}
      {bundesland && <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.3 }}>{bundesland}</div>}
    </div>
  );
}

function AutoFit({ features }) {
  const map = useMap();
  useEffect(() => {
    if (!features.length) return;
    const layer = L.geoJSON({ type: 'FeatureCollection', features });
    const bounds = layer.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
    layer.remove();
  }, [map, features]);
  return null;
}

export default function NetBankPage() {
  const [features, setFeatures]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [stepFilter, setStepFilter] = useState({ lv: false, mv: false, pf: false });
  const [bundesland, setBundesland] = useState('');   // '' = all
  const [areaType, setAreaType]     = useState('');   // '' | 'urban' | 'rural'
  const [confirmTarget, setConfirmTarget] = useState(null); // feature pending delete
  const [deletingId, setDeletingId]       = useState(null); // task_id in flight
  const [deleteError, setDeleteError]     = useState('');

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await apiFetch('/generated-areas', { signal: controller.signal });
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        const data = await res.json();
        setFeatures(data.features || []);
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError('Could not load generated areas.');
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  // Sorted unique Bundesländer present in the data
  const allBundeslaender = useMemo(() => {
    const s = new Set(features.map(f => f.properties?.bundesland).filter(Boolean));
    return [...s].sort();
  }, [features]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return features.filter(f => {
      const p = f.properties || {};
      if (q && !(p.name || '').toLowerCase().includes(q)) return false;
      if (stepFilter.lv  && !p.has_lv)  return false;
      if (stepFilter.mv  && !p.has_mv)  return false;
      if (stepFilter.pf  && !p.has_pf)  return false;
      if (bundesland && p.bundesland !== bundesland) return false;
      if (areaType === 'urban' && !p.is_urban)  return false;
      if (areaType === 'rural' &&  p.is_urban)  return false;
      return true;
    });
  }, [features, search, stepFilter, bundesland, areaType]);

  const geoJsonData = useMemo(
    () => ({ type: 'FeatureCollection', features: filtered }),
    [filtered]
  );

  const geoJsonKey = useMemo(
    () => filtered.map(f => f.properties?.task_id).join(','),
    [filtered]
  );

  const openResult = (f) => {
    const { task_id, name } = f.properties || {};
    if (!task_id) return;
    const url = `?mode=result&task=${encodeURIComponent(task_id)}&taskName=${encodeURIComponent(name || task_id)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const toggleStep = (key) => setStepFilter(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleAreaType = (val) => setAreaType(prev => prev === val ? '' : val);

  const requestDelete = (f, e) => {
    if (e) e.stopPropagation();
    setDeleteError('');
    setConfirmTarget(f);
  };

  const confirmDelete = async () => {
    const f = confirmTarget;
    const taskId = f?.properties?.task_id;
    if (!taskId) { setConfirmTarget(null); return; }
    setDeletingId(taskId);
    setDeleteError('');
    try {
      const res = await apiFetch(
        `/tasks/${encodeURIComponent(taskId)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        let msg = `Server error ${res.status}`;
        try { const j = await res.json(); if (j?.message) msg = j.message; } catch { /* ignore */ }
        throw new Error(msg);
      }
      setFeatures(prev => prev.filter(x => x.properties?.task_id !== taskId));
      setConfirmTarget(null);
    } catch (err) {
      setDeleteError(err.message || 'Could not delete the area.');
    } finally {
      setDeletingId(null);
    }
  };

  const onEachFeature = (feature, layer) => {
    const p  = feature.properties || {};
    const { kg, gemeinde, bundesland: bl } = nameParts(p.name);
    const badges = [p.has_lv && 'LV', p.has_mv && 'MV', p.has_pf && 'PF'].filter(Boolean).join(' · ') || 'Graph only';
    layer.bindPopup(
      `<strong>${kg}</strong>${gemeinde ? `<br/><span style="font-size:12px">${gemeinde}</span>` : ''}` +
      `${bl ? `<br/><span style="font-size:11px;color:#999">${bl}</span>` : ''}<br/>${badges}`
    );
    layer.on('click', () => openResult(feature));
  };

  const selectStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(144,202,249,0.35)',
    borderRadius: 6, color: '#e3f2fd',
    padding: '7px 28px 7px 10px', fontSize: 13,
    appearance: 'none', cursor: 'pointer',
  };

  return (
    <div className="netbank-page">
      <div className="netbank-header">
        <h2>Generated Areas</h2>
      </div>
      <div className="netbank-layout">

        {/* ── Controls sidebar ── */}
        <div className="netbank-controls">

          {/* Search */}
          <div className="netbank-control-group">
            <label className="netbank-label" htmlFor="nb-search">Search</label>
            <input
              id="nb-search"
              type="text"
              value={search}
              placeholder="Search by area name…"
              onChange={e => setSearch(e.target.value)}
              autoComplete="off"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          {/* Bundesland */}
          <div className="netbank-control-group">
            <label className="netbank-label" htmlFor="nb-bl">Bundesland</label>
            <div style={{ position: 'relative' }}>
              <select
                id="nb-bl"
                value={bundesland}
                onChange={e => setBundesland(e.target.value)}
                style={selectStyle}
              >
                <option value="">All</option>
                {allBundeslaender.map(bl => (
                  <option key={bl} value={bl}>{bl}</option>
                ))}
              </select>
              <span style={{
                position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
                pointerEvents: 'none', color: '#94a3b8', fontSize: 11,
              }}>▾</span>
            </div>
          </div>

          {/* Area type */}
          <div className="netbank-control-group">
            <span className="netbank-label">Area type</span>
            <div className="netbank-toggle-buttons">
              {[['urban', 'Urban'], ['rural', 'Rural']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`netbank-toggle-button ${areaType === key ? 'active' : ''}`}
                  onClick={() => toggleAreaType(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Step filter */}
          <div className="netbank-control-group">
            <span className="netbank-label">Must have</span>
            <div className="netbank-toggle-buttons">
              {[['lv', 'LV'], ['mv', 'MV'], ['pf', 'PF']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`netbank-toggle-button ${stepFilter[key] ? 'active' : ''}`}
                  onClick={() => toggleStep(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="netbank-status">
            {loading && <span>Loading…</span>}
            {!loading && !error && (
              <span>Showing {filtered.length} of {features.length} areas</span>
            )}
            {error && <span className="netbank-error">{error}</span>}
          </div>

          {/* Legend */}
          <div className="netbank-control-group" style={{ marginTop: 16 }}>
            <span className="netbank-label">Legend</span>
            {LEGEND.map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, fontSize: 12 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: color, flexShrink: 0 }} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Map + cards ── */}
        <div className="netbank-map-column">
          <div className="netbank-map-wrapper">
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              className="netbank-map"
              preferCanvas
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              <AutoFit features={filtered} />
              <GeoJSON
                key={geoJsonKey}
                data={geoJsonData}
                style={f => {
                  const color = stepColor(f?.properties);
                  return { color, weight: 2, fillColor: color, fillOpacity: 0.35 };
                }}
                onEachFeature={onEachFeature}
              />
            </MapContainer>
          </div>

          {/* Result cards */}
          <div className="netbank-results">
            {!loading && !error && filtered.length === 0 && (
              <p>No areas match the selected filters.</p>
            )}
            {!loading && filtered.length > 0 && (
              <ul>
                {filtered.map(f => {
                  const p = f.properties || {};
                  const badges = [p.has_lv && 'LV', p.has_mv && 'MV', p.has_pf && 'PF'].filter(Boolean);
                  return (
                    <li key={p.task_id}>
                      <button
                        type="button"
                        className="netbank-delete-btn"
                        title="Delete this area"
                        disabled={deletingId === p.task_id}
                        onClick={(e) => requestDelete(f, e)}
                      >
                        {deletingId === p.task_id ? '…' : '🗑'}
                      </button>
                      <button
                        type="button"
                        className="netbank-result-card"
                        onClick={() => openResult(f)}
                      >
                        <AreaName name={p.name} />
                        <span className="netbank-result-meta" style={{ marginTop: 6, display: 'block' }}>
                          {badges.length ? badges.join(' · ') : 'Graph only'}
                        </span>
                        <span className="netbank-result-cta">Open results →</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {confirmTarget && (
        <div
          className="netbank-modal-overlay"
          onClick={() => { if (!deletingId) setConfirmTarget(null); }}
        >
          <div className="netbank-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px' }}>Delete this area?</h3>
            <AreaName name={confirmTarget.properties?.name} style={{ marginBottom: 12 }} />
            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#cbd5e1' }}>
              This permanently removes the grid from the database and the P drive.
              This cannot be undone.
            </p>
            {deleteError && (
              <p className="netbank-error" style={{ margin: '0 0 8px' }}>{deleteError}</p>
            )}
            <div className="netbank-modal-actions">
              <button
                type="button"
                className="netbank-toggle-button"
                onClick={() => setConfirmTarget(null)}
                disabled={!!deletingId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="netbank-danger-button"
                onClick={confirmDelete}
                disabled={!!deletingId}
              >
                {deletingId ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
